import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api.js'
import { useDebounced } from '@/lib/hooks.js'
import type { SearchResult } from '@garage/shared'

/**
 * Global search overlay, mounted once in AppShell.
 * - Command-palette style: overlay with scrim, centered panel near viewport top
 * - Debounced search via api.search(q)
 * - Results preserve server order (flat, pre-ranked); grouped for display only
 * - Keyboard: Arrow keys to navigate, Enter to select, Escape to close, Ctrl/Cmd+K opens from anywhere
 * - State is driven from outside (AppShell owns it), allowing multiple controls to open/close it
 * - Single code path across all breakpoints via portal
 */

interface GroupedResult {
  type: 'car' | 'client' | 'jobcard'
  id: string
  title: string
  subtitle: string
  clientId?: string | null
}

interface GlobalSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The API returns one flat, pre-ranked list. Build a display structure that
 * groups for headings but preserves the server's order within each group.
 */
function groupForDisplay(results: GroupedResult[]): Array<{
  group?: GroupedResult['type']
  items: GroupedResult[]
}> {
  const groups: Array<{ group?: GroupedResult['type']; items: GroupedResult[] }> = []
  let currentGroup: GroupedResult['type'] | undefined = undefined
  let currentItems: GroupedResult[] = []

  for (const result of results) {
    const resultGroup = result.type

    // Start a new group if type changes.
    if (resultGroup !== currentGroup) {
      if (currentItems.length > 0) {
        groups.push({ group: currentGroup, items: currentItems })
      }
      currentGroup = resultGroup
      currentItems = []
    }

    currentItems.push(result)
  }

  if (currentItems.length > 0) {
    groups.push({ group: currentGroup, items: currentItems })
  }

  return groups
}

const GROUP_LABEL_KEYS: Record<GroupedResult['type'], string> = {
  car: 'globalSearch.groupCars',
  client: 'globalSearch.groupClients',
  jobcard: 'globalSearch.groupJobcards',
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounced(query, 250)
  const [flatResults, setFlatResults] = useState<GroupedResult[]>([])
  const [loading, setLoading] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousActiveElement = useRef<Element | null>(null)

  // Fetch results when debounced query changes.
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setFlatResults([])
      setHighlightIndex(-1)
      return
    }

    const fetchResults = async () => {
      setLoading(true)
      try {
        const response = await api.search(debouncedQuery)
        // Filter to skip any result without a valid type, then map to grouped results
        // while preserving the server's rank order.
        const mapped: GroupedResult[] = response.results
          .filter(
            (r): r is Extract<SearchResult, { type: 'car' | 'client' | 'jobcard' }> =>
              r.type === 'car' || r.type === 'client' || r.type === 'jobcard',
          )
          .map(r => ({
            type: r.type,
            id: r.id,
            title: r.title,
            subtitle: r.subtitle,
            clientId: r.type === 'car' ? r.clientId : undefined,
          }))
        setFlatResults(mapped)
        setHighlightIndex(-1)
      } catch {
        setFlatResults([])
      } finally {
        setLoading(false)
      }
    }

    fetchResults()
  }, [debouncedQuery])

  // Store previous focus and manage open state: lock scroll, autofocus input.
  useEffect(() => {
    if (!open) return

    previousActiveElement.current = document.activeElement
    document.body.style.overflow = 'hidden'

    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)

    return () => {
      document.body.style.overflow = ''
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus()
      }
    }
  }, [open])

  // Ctrl/Cmd+K opens from anywhere (window-level listener, not input-level).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        onOpenChange(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onOpenChange])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(prev => (prev + 1) % Math.max(flatResults.length, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(prev => (prev === -1 ? flatResults.length - 1 : prev - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < flatResults.length) {
        const result = flatResults[highlightIndex]
        if (result) {
          handleSelectResult(result)
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onOpenChange(false)
    }
  }

  const handleSelectResult = (result: GroupedResult) => {
    if (result.type === 'car') {
      if (result.clientId) {
        navigate(`/clients/${result.clientId}`)
      }
    } else if (result.type === 'client') {
      navigate(`/clients/${result.id}`)
    } else if (result.type === 'jobcard') {
      navigate(`/jobcards/${result.id}`)
    }
    setQuery('')
    onOpenChange(false)
  }

  const handleClickScrim = () => {
    onOpenChange(false)
  }

  // Group results for display, preserving order.
  const groupedDisplay = groupForDisplay(flatResults)

  // Render results panel with grouped headings and rows.
  const renderResultsContent = () => {
    if (loading) {
      return (
        <div className="px-3 py-8 text-center text-on-surface-variant text-body-md">
          {t('globalSearch.searching')}
        </div>
      )
    }

    if (!query.trim()) {
      return (
        <div className="px-3 py-8 text-center text-on-surface-variant text-body-md">
          {t('globalSearch.searchPrompt')}
        </div>
      )
    }

    if (flatResults.length === 0) {
      return (
        <div className="px-3 py-8 text-center text-on-surface-variant text-body-md">
          {t('storage.noMatches')}
        </div>
      )
    }

    return (
      <>
        {groupedDisplay.map((group, groupIndex) => {
          // Pre-calculate cumulative index for results before this group.
          let cumulativeIndex = 0
          for (let i = 0; i < groupIndex; i++) {
            cumulativeIndex += groupedDisplay[i]!.items.length
          }

          return (
            <div key={group.group || 'ungrouped'}>
              {group.group && (
                <div className="text-label-sm text-on-surface-subtle uppercase tracking-wide px-3 py-1.5">
                  {t(GROUP_LABEL_KEYS[group.group])}
                </div>
              )}
              {group.items.map((item, itemIndex) => {
                const globalIndex = cumulativeIndex + itemIndex
                const isHighlighted = highlightIndex === globalIndex

                return (
                  <div
                    key={item.id}
                    role="option"
                    aria-selected={isHighlighted}
                    onClick={() => handleSelectResult(item)}
                    className={`px-3 py-2 cursor-pointer interactive ${
                      isHighlighted ? 'bg-surface-high' : 'hover:bg-surface-high'
                    }`}
                  >
                    <div className="text-body-md text-on-surface">
                      {item.type === 'car' || item.type === 'jobcard' ? (
                        <span className="ref">{item.title}</span>
                      ) : (
                        item.title
                      )}
                    </div>
                    <div className="text-body-sm text-on-surface-variant">{item.subtitle}</div>
                  </div>
                )
              })}
            </div>
          )
        })}

        {/* Footer hint showing keyboard shortcuts. */}
        <div className="border-t border-outline-variant px-3 py-2 text-label-sm text-on-surface-subtle flex gap-2 flex-wrap">
          <span>↑↓ {t('globalSearch.toNavigate')}</span>
          <span>·</span>
          <span>↵ {t('globalSearch.toOpen')}</span>
          <span>·</span>
          <span>esc {t('globalSearch.toClose')}</span>
        </div>
      </>
    )
  }

  if (!open) return null

  return createPortal(
    // Scrim with backdrop blur, using inline style for opacity since it doesn't work with CSS variables
    <div
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      className="fixed inset-0 flex items-start justify-center z-50 backdrop-blur-sm"
      onClick={handleClickScrim}
      role="presentation"
      aria-hidden="true"
    >
      {/* Panel positioned near the top of viewport. */}
      <div
        className="bg-surface-container border border-outline-variant rounded-xl shadow-elev-3 w-full max-w-xl mx-4 mt-[12vh] max-h-[60vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.search')}
      >
        {/* Input area with bottom border instead of separate box. */}
        <input
          ref={inputRef}
          type="text"
          placeholder={t('globalSearch.searchEllipsis')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 bg-surface-container text-on-surface placeholder-on-surface-variant border-b border-outline-variant focus:outline-none interactive"
          aria-label={t('nav.search')}
        />

        {/* Results area: scrollable. */}
        <div className="flex-1 overflow-y-auto" role="listbox" aria-activedescendant={highlightIndex >= 0 ? `search-result-${highlightIndex}` : undefined}>
          {renderResultsContent()}
        </div>
      </div>
    </div>,
    document.body,
  )
}
